// Gradient Builder - Flutter Code Generator
// Converts GradientConfig to Flutter Dart code

import type {
  GradientConfig,
  LinearGradientConfig,
  RadialGradientConfig,
  SweepGradientConfig,
  ColorStop,
} from "../model/gradient";

/**
 * Convert hex color to Flutter Color format
 * #RRGGBB -> Color(0xFFRRGGBB)
 */
function hexToFlutterColor(hex: string): string {
  // Remove # if present
  const cleanHex = hex.replace(/^#/, '');

  // Add full opacity (FF) prefix
  return `Color(0xFF${cleanHex.toUpperCase()})`;
}

/**
 * Convert angle in degrees to Flutter Alignment
 * 0° = left to right, 90° = top to bottom, etc.
 */
function angleToAlignment(angle: number): { begin: string; end: string } {
  // Normalize angle to 0-360
  const normalized = ((angle % 360) + 360) % 360;

  // Convert to radians
  const rad = (normalized * Math.PI) / 180;

  // Calculate alignment coordinates (-1 to 1)
  const x = Math.cos(rad);
  const y = Math.sin(rad);

  // Determine begin and end points
  const beginX = -x;
  const beginY = -y;
  const endX = x;
  const endY = y;

  // Common alignment shortcuts
  const alignments: Record<string, string> = {
    '0,0': 'Alignment.center',
    '-1,-1': 'Alignment.topLeft',
    '0,-1': 'Alignment.topCenter',
    '1,-1': 'Alignment.topRight',
    '-1,0': 'Alignment.centerLeft',
    '1,0': 'Alignment.centerRight',
    '-1,1': 'Alignment.bottomLeft',
    '0,1': 'Alignment.bottomCenter',
    '1,1': 'Alignment.bottomRight',
  };

  // Round to 1 decimal for common cases
  const roundCoord = (v: number) => Math.round(v * 10) / 10;
  const beginKey = `${roundCoord(beginX)},${roundCoord(beginY)}`;
  const endKey = `${roundCoord(endX)},${roundCoord(endY)}`;

  return {
    begin: alignments[beginKey] || `Alignment(${beginX.toFixed(2)}, ${beginY.toFixed(2)})`,
    end: alignments[endKey] || `Alignment(${endX.toFixed(2)}, ${endY.toFixed(2)})`,
  };
}

/**
 * Convert center position string to Flutter Alignment
 */
function centerToAlignment(center: string): string {
  const alignmentMap: Record<string, string> = {
    center: 'Alignment.center',
    topCenter: 'Alignment.topCenter',
    bottomCenter: 'Alignment.bottomCenter',
    centerLeft: 'Alignment.centerLeft',
    centerRight: 'Alignment.centerRight',
  };

  return alignmentMap[center] || 'Alignment.center';
}

/**
 * Format color stops array for Flutter
 */
function formatColorStops(colors: ColorStop[]): string {
  const colorLines = colors.map(stop => `    ${hexToFlutterColor(stop.color)},`);
  return colorLines.join('\n');
}

/**
 * Generate Linear Gradient Flutter code
 */
function generateLinearGradient(config: LinearGradientConfig): string {
  const { begin, end } = angleToAlignment(config.angle);

  return `LinearGradient(
  colors: [
${formatColorStops(config.colors)}
  ],
  begin: ${begin},
  end: ${end},
)`;
}

/**
 * Generate Radial Gradient Flutter code
 */
function generateRadialGradient(config: RadialGradientConfig): string {
  const center = centerToAlignment(config.center);

  return `RadialGradient(
  colors: [
${formatColorStops(config.colors)}
  ],
  center: ${center},
  radius: ${config.radius.toFixed(1)},
)`;
}

/**
 * Generate Sweep Gradient Flutter code
 */
function generateSweepGradient(config: SweepGradientConfig): string {
  const center = centerToAlignment(config.center);

  return `SweepGradient(
  colors: [
${formatColorStops(config.colors)}
  ],
  center: ${center},
  startAngle: ${config.startAngle.toFixed(2)},
  endAngle: ${config.endAngle.toFixed(2)},
)`;
}

/**
 * Main function: Generate Flutter code from GradientConfig
 */
export function generateFlutterCode(config: GradientConfig): string {
  switch (config.type) {
    case 'linear':
      return generateLinearGradient(config);
    case 'radial':
      return generateRadialGradient(config);
    case 'sweep':
      return generateSweepGradient(config);
    default:
      throw new Error(`Unknown gradient type: ${(config as any).type}`);
  }
}
